import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/folders`);
      setFolders(response.data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const fetchProject = useCallback(async (projectId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${projectId}`);
      setCurrentProject(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createProject = async (projectData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/projects`, projectData, {
        headers: getAuthHeaders()
      });
      setProjects(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
        throw err;
      }
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectId, projectData) => {
    // Block saves on demo projects
    const project = projects.find(p => p.id === projectId) || currentProject;
    if (project?.is_demo) {
      setShowPaywall(true);
      throw new Error('Demo project is read-only');
    }
    try {
      setLoading(true);
      const response = await axios.put(`${API}/projects/${projectId}`, projectData, {
        headers: getAuthHeaders()
      });
      setProjects(prev => prev.map(p => p.id === projectId ? response.data : p));
      setCurrentProject(response.data);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
        throw err;
      }
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
        throw err;
      }
      setError(err.message);
      throw err;
    }
  };

  const createFolder = async (folderData) => {
    try {
      const response = await axios.post(`${API}/folders`, folderData);
      setFolders(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      setFolders(prev => prev.filter(f => f.id !== folderId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateFolder = async (folderId, folderData) => {
    try {
      const response = await axios.put(`${API}/folders/${folderId}`, folderData);
      setFolders(prev => prev.map(f => f.id === folderId ? response.data : f));
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getDashboard = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/dashboard`);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getCalculation = async (projectId, type) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/calculations/${type}`);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.image;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchFolders();
    }
  }, [user, fetchProjects, fetchFolders]);

  const value = {
    projects,
    folders,
    currentProject,
    loading,
    error,
    showPaywall,
    setShowPaywall,
    fetchProjects,
    fetchFolders,
    fetchProject,
    createProject,
    updateProject,
    deleteProject,
    createFolder,
    updateFolder,
    deleteFolder,
    getDashboard,
    getCalculation,
    uploadImage,
    setCurrentProject
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
